/**
 * Shared helpers for the Send-Forms wizards (single-recipient dialog and
 * the campaign-style broadcast builder).
 *
 * Keep these in lockstep with the backend `validate_bcc()` logic on
 * ConsentRequestCreateSerializer / ConsentBroadcastCreateSerializer so the
 * client and server agree on what "20 emails, lowercased, deduped" means.
 */

// Strip / lowercase / dedupe a list of email strings (matches backend
// validate_bcc behaviour — keep client and server in sync).
export const normaliseEmails = (input) => {
    const seen = new Set();
    const result = [];
    (input || []).forEach((raw) => {
        const addr = String(raw || '').trim().toLowerCase();
        if (!addr || seen.has(addr)) return;
        seen.add(addr);
        result.push(addr);
    });
    return result.slice(0, 20); // server caps at 20
};

export const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e || '');

// Channel label/icon helpers — used in both wizards.
export const channelLabel = (ch) => ({
    EMAIL: 'Email',
    WHATSAPP: 'WhatsApp',
    COPY: 'Copy link',
    QR: 'QR code',
    SMS: 'SMS',
}[ch] || 'Send');

export const channelIcon = (ch) => ({
    EMAIL: 'solar:letter-bold-duotone',
    WHATSAPP: 'mdi:whatsapp',
    COPY: 'solar:link-round-bold-duotone',
    QR: 'solar:qr-code-bold-duotone',
    SMS: 'material-symbols:sms',
}[ch] || 'eva:paper-plane-fill');

// Lead → Patient text rewrite (used for user-visible copy only — never for
// state keys, prop names, or API payload field names).
export const leadCopyToPatient = (text = '') => String(text)
    .replace(/Leads/g, 'Patients')
    .replace(/Lead/g, 'Patient')
    .replace(/leads/g, 'patients')
    .replace(/lead/g, 'patient');

/**
 * Only default to the practice's configured template when it is actually in the
 * eligible (FORMS-tagged) list the dialog fetched. A stale default — e.g. a
 * template since re-tagged — makes the BE reject the send with 400
 * "email_template_id must reference an EMAIL template tagged with category='FORMS'".
 * Returns the eligible id, or null so the UI prompts "Pick an email template".
 */
export const pickEligibleDefaultTemplateId = (defaultId, eligibleTemplates) => {
    if (defaultId === null || defaultId === undefined || defaultId === '') return null;
    const match = (eligibleTemplates || []).find((t) => String(t?.id) === String(defaultId));
    return match ? match.id : null;
};

/**
 * Friendly copy for the BE's template-eligibility 400; null if unrelated.
 * Only relabels messages that are actually the FORMS-eligibility error; any
 * other field error (e.g. "This field is required.") is returned verbatim.
 */
const FORMS_ELIGIBILITY_RE = /tagged with category|FORMS/i;
const firstText = (raw) => {
    const text = Array.isArray(raw) ? raw[0] : raw;
    return typeof text === 'string' && text ? text : null;
};
export const friendlyTemplateError = (data) => {
    const emailText = firstText(data?.email_template_id);
    if (emailText) {
        return FORMS_ELIGIBILITY_RE.test(emailText)
            ? 'The selected email template is no longer tagged for Forms. Please pick another email template (or tag one as Forms in Campaigns Manager > Templates).'
            : emailText;
    }
    const waText = firstText(data?.whatsapp_template_id);
    if (waText) {
        return FORMS_ELIGIBILITY_RE.test(waText)
            ? 'The selected WhatsApp template is not available for Forms. Please pick another template.'
            : waText;
    }
    return null;
};
